import React, { useRef, useState } from "react";
import { Form, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ajukanIzin } from "../services/izinService";
import BottomNavbar from "../components/BottomNavbar";
import imageCompression from "browser-image-compression";

const MAX_LAMPIRAN_BYTES = 100 * 1024;

function todayLocalISO() {
  const today = new Date();
  const month = `${today.getMonth() + 1}`.padStart(2, "0");
  const day = `${today.getDate()}`.padStart(2, "0");
  return `${today.getFullYear()}-${month}-${day}`;
}

async function compressLampiran(file) {
  const attempts = [
    { dimension: 1280, quality: 0.82 },
    { dimension: 1080, quality: 0.72 },
    { dimension: 960, quality: 0.64 },
    { dimension: 840, quality: 0.56 },
    { dimension: 720, quality: 0.48 },
    { dimension: 640, quality: 0.4 },
    { dimension: 512, quality: 0.34 },
    { dimension: 400, quality: 0.28 },
  ];

  for (const attempt of attempts) {
    const compressed = await imageCompression(file, {
      maxSizeMB: 0.1,
      maxWidthOrHeight: attempt.dimension,
      initialQuality: attempt.quality,
      fileType: "image/jpeg",
      useWebWorker: true,
    });

    if (compressed.size <= MAX_LAMPIRAN_BYTES) {
      return new File([compressed], "bukti-izin.jpg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
    }
  }

  throw new Error("Foto belum bisa diperkecil hingga maksimal 100 KB.");
}

function Izin() {
  const navigate = useNavigate();
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  const [keterangan, setKeterangan] = useState("");
  const [jenisIzin, setJenisIzin] = useState("sakit");
  const [tanggalIzin, setTanggalIzin] = useState(() => {
    const today = new Date();
    const month = `${today.getMonth() + 1}`.padStart(2, "0");
    const day = `${today.getDate()}`.padStart(2, "0");
    return `${today.getFullYear()}-${month}-${day}`;
  });
  const [persetujuanData, setPersetujuanData] = useState(true);
  const [foto, setFoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitStage, setSubmitStage] = useState("compress");
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [pesan, setPesan] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) {
      setFoto(null);
      setPesan({ type: "danger", text: "Lampiran harus berupa foto JPG atau PNG." });
      e.target.value = "";
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setFoto(null);
      setPesan({ type: "danger", text: "Ukuran foto maksimal 20 MB sebelum dikompres." });
      e.target.value = "";
      return;
    }

    setFoto(file);
    setPesan(null);
    e.target.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!keterangan.trim()) {
      return setPesan({ type: "danger", text: "Keterangan wajib diisi." });
    }

    if (!foto || !tanggalIzin || !persetujuanData) {
      return setPesan({
        type: "danger",
        text: "Tanggal, lampiran, dan pernyataan wajib dilengkapi.",
      });
    }

    setLoading(true);
    setSubmitStage("compress");
    try {
      const fotoTerkompres = await compressLampiran(foto);
      setSubmitStage("send");

      const result = await ajukanIzin({
        fotoFile: fotoTerkompres,
        keterangan,
        jenisIzin,
        tanggalIzin,
      });

      if (result.success) {
        setSuccessMessage(
          result.data?.message ||
            `Pengajuan ${jenisIzin} berhasil dikirim dan menunggu verifikasi guru.`,
        );
        setSuccess(true);
        setKeterangan("");
        setFoto(null);
      } else {
        setPesan({
          type: "danger",
          text: result.message || "Gagal mengirim izin.",
        });
      }
    } catch (error) {
      setPesan({
        type: "danger",
        text: error.message || "Gagal memproses lampiran.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="app-mobile-page izin-page">
      <div
        className="izin-page-header d-flex align-items-center px-3"
        style={{ minHeight: 88, background: "#103F94", color: "white" }}
      >
        <button
          type="button"
          aria-label="Kembali"
          onClick={() => navigate(-1)}
          style={{
            background: "rgba(255,255,255,.12)",
            border: "none",
            borderRadius: 12,
            width: 44,
            height: 44,
            fontSize: "24px",
            color: "white",
            cursor: "pointer",
            padding: 0,
            marginRight: "12px",
          }}
        >
          ←
        </button>
        <div>
          <h5 className="mb-1 fw-bold">+ Izin</h5>
          <div style={{ fontSize: 13, opacity: 0.85 }}>Ajukan ketidakhadiran PKL</div>
        </div>
      </div>

      <section className="app-content py-4">
        {pesan && (
          <Alert variant={pesan.type} dismissible onClose={() => setPesan(null)}>
            {pesan.text}
          </Alert>
        )}

        <h4 className="fw-bold mb-1" style={{ color: "#17324D", fontSize: 20 }}>
          Form Pengajuan Izin
        </h4>
        <p className="mb-4" style={{ color: "#657895", fontSize: 14 }}>
          Lengkapi alasan dan lampiran bukti agar pengajuan dapat diverifikasi Guru Pembimbing.
        </p>

        <Form onSubmit={handleSubmit}>
          <Form.Label className="fw-semibold mb-2" style={{ color: "#17324D" }}>
            Jenis Pengajuan *
          </Form.Label>
          <div className="d-flex gap-2 mb-3">
            {[{ value: "sakit", label: "Sakit", icon: "♡" }, { value: "izin", label: "Izin", icon: "◷" }].map((option) => (
              <Button
                key={option.value}
                type="button"
                variant={jenisIzin === option.value ? "primary" : "light"}
                onClick={() => setJenisIzin(option.value)}
                disabled={loading}
                className="flex-fill fw-semibold"
                style={{
                  minHeight: 52,
                  color: jenisIzin === option.value ? "#155EEF" : "#60718F",
                  background: jenisIzin === option.value ? "#EAF1FF" : "white",
                  border: `1px solid ${jenisIzin === option.value ? "#155EEF" : "#C9D7EA"}`,
                  borderRadius: 12,
                }}
              >
                <span className="me-2" aria-hidden="true">{option.icon}</span>{option.label}
              </Button>
            ))}
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ color: "#17324D" }}>
              Tanggal *
            </Form.Label>
            <Form.Control
              type="date"
              value={tanggalIzin}
              max={todayLocalISO()}
              onChange={(e) => setTanggalIzin(e.target.value)}
              disabled={loading}
              required
              className="bg-white"
              style={{ minHeight: 52, borderColor: "#C9D7EA", borderRadius: 12 }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold" style={{ color: "#17324D" }}>
              Alasan *
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={255}
              required
              placeholder={jenisIzin === "sakit" ? "Jelaskan kondisi dan kebutuhan istirahat." : "Jelaskan keperluan izin."}
              value={keterangan}
              onChange={(e) => setKeterangan(e.target.value)}
              disabled={loading}
              className="bg-white"
              style={{ borderColor: "#C9D7EA", borderRadius: 12 }}
            />
          </Form.Group>

          <Form.Label className="fw-semibold mb-2" style={{ color: "#17324D" }}>
            Lampiran Bukti *
          </Form.Label>
          <div className="text-center p-3 mb-1 bg-white" style={{ border: "1px dashed #155EEF", borderRadius: 14 }}>
            {foto ? (
              <div className="small fw-semibold mb-2" style={{ color: "#17324D" }}>{foto.name}</div>
            ) : (
              <>
                <div className="mx-auto mb-2 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, borderRadius: 24, background: "#EAF1FF", color: "#155EEF", fontSize: 22 }}>＋</div>
                <div className="fw-semibold" style={{ color: "#17324D" }}>Unggah foto bukti</div>
                <div className="small" style={{ color: "#657895" }}>Format JPG/PNG, maksimal 20 MB sebelum kompresi</div>
              </>
            )}
            <input ref={cameraInput} type="file" accept="image/jpeg,image/png" capture="environment" onChange={handleFileChange} hidden disabled={loading} />
            <input ref={galleryInput} type="file" accept="image/jpeg,image/png" onChange={handleFileChange} hidden disabled={loading} />
            <div className="d-flex gap-2 mt-3">
              <Button type="button" className="flex-fill fw-semibold" onClick={() => cameraInput.current?.click()} disabled={loading} style={{ minHeight: 48, color: "#155EEF", background: "#EAF1FF", border: 0, borderRadius: 12 }}>
                Kamera
              </Button>
              <Button type="button" variant="outline-secondary" className="flex-fill fw-semibold" onClick={() => galleryInput.current?.click()} disabled={loading} style={{ minHeight: 48, borderColor: "#C9D7EA", borderRadius: 12 }}>
                Galeri
              </Button>
            </div>
          </div>
          {!foto && <div className="small text-danger mb-3">ⓘ Lampiran wajib ditambahkan sebelum dikirim.</div>}

          <Form.Check
            type="checkbox"
            checked={persetujuanData}
            onChange={(e) => setPersetujuanData(e.target.checked)}
            disabled={loading}
            label="Saya menyatakan data dan lampiran yang dikirim adalah benar."
            className="p-3 my-3 rounded"
            style={{ background: "#EAF1FF", color: "#657895", fontSize: 13 }}
          />

          <Button
            type="submit"
            className="w-100 fw-bold"
            style={{ minHeight: 54, backgroundColor: "#155EEF", border: "none", borderRadius: 12 }}
            disabled={loading || !persetujuanData}
          >
            {loading
              ? submitStage === "compress"
                ? "Mengompres foto..."
                : "Mengirim pengajuan..."
              : "➤  Kirim Pengajuan"}
          </Button>
        </Form>
        <p className="text-center small mt-3 mb-0" style={{ color: "#657895" }}>
          Pengajuan akan masuk ke antrean verifikasi Guru Pembimbing.
        </p>
      </section>

      <BottomNavbar />

      {loading && (
        <div
          role="status"
          aria-live="polite"
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-4"
          style={{ zIndex: 2000, background: "rgba(16, 39, 72, .48)", backdropFilter: "blur(5px)" }}
        >
          <div
            className="w-100 text-center bg-white p-4"
            style={{ maxWidth: 360, borderRadius: 24, boxShadow: "0 24px 70px rgba(17, 44, 84, .24)" }}
          >
            <div
              className="mx-auto mb-3 d-flex align-items-center justify-content-center"
              style={{ width: 68, height: 68, borderRadius: 22, color: "#155EEF", background: "#EAF1FF" }}
            >
              <span className="spinner-border" aria-hidden="true" style={{ width: 30, height: 30, borderWidth: 3 }} />
            </div>
            <h5 className="fw-bold mb-2" style={{ color: "#17324D" }}>
              {submitStage === "compress" ? "Mengoptimalkan foto" : "Mengirim pengajuan"}
            </h5>
            <p className="small mb-4" style={{ color: "#657895" }}>
              {submitStage === "compress"
                ? "Lampiran sedang dikompres agar maksimal 100 KB."
                : "Foto dan izin sedang dikirim ke Guru Pembimbing."}
            </p>
            <div className="progress mb-3" style={{ height: 6, borderRadius: 8, background: "#EAF1FF" }}>
              <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: "100%", background: "#155EEF" }} />
            </div>
            <div className="d-flex justify-content-center align-items-center gap-2 small">
              <span className="fw-semibold" style={{ color: "#155EEF" }}>
                {submitStage === "send" ? "✓" : "◉"} Kompres
              </span>
              <span style={{ color: "#C9D7EA" }}>—</span>
              <span style={{ color: submitStage === "send" ? "#155EEF" : "#8191A8", fontWeight: submitStage === "send" ? 700 : 500 }}>
                {submitStage === "send" ? "◉" : "○"} Kirim
              </span>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div
          role="dialog"
          aria-modal="true"
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-4"
          style={{ zIndex: 2001, background: "rgba(16, 39, 72, .48)", backdropFilter: "blur(5px)" }}
        >
          <div
            className="w-100 text-center bg-white p-4"
            style={{ maxWidth: 380, borderRadius: 24, boxShadow: "0 24px 70px rgba(17, 44, 84, .24)" }}
          >
            <div className="mx-auto mb-3 d-flex align-items-center justify-content-center fw-bold" style={{ width: 68, height: 68, borderRadius: 34, color: "#079455", background: "#E5F6EF", fontSize: 34 }}>
              ✓
            </div>
            <h4 className="fw-bold mb-2" style={{ color: "#17324D" }}>Pengajuan terkirim</h4>
            <p className="small mb-3" style={{ color: "#657895" }}>{successMessage}</p>
            <div className="d-inline-block rounded-pill px-3 py-2 mb-4 fw-semibold small" style={{ color: "#079455", background: "#E5F6EF" }}>
              Menunggu verifikasi guru
            </div>
            <Button className="w-100 fw-bold" onClick={() => navigate("/dashboard")} style={{ minHeight: 50, background: "#155EEF", border: 0, borderRadius: 12 }}>
              Kembali ke beranda
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}

export default Izin;
